// src/components/AdminDashboard.js
import React, { useEffect, useState } from 'react';
import adminService from '../services/adminService';
import tournamentService from '../services/tournamentService';
import matchService from '../services/matchService';
import '../styles/AdminDashboard.css';
import { toast } from 'sonner';
import { FiBarChart2, FiTarget, FiRefreshCw, FiAward, FiBell } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

// Stavy probíhajícího zápasu – jen pro odlišení „živě" v nabídce. Vlastní filtr
// zápasů (skončené + probíhající) řeší backend endpoint /recalculable-matches.
const IN_PROGRESS_MATCH_STATUSES = ['_1H', 'HT', '_2H', 'ET', 'P', 'P1', 'P2', 'P3', 'OT', 'PT', 'BT', 'INTR', 'LIVE'];

const AdminDashboard = () => {
    const { t } = useTranslation();
    const [health, setHealth] = useState(null);
    const [info, setInfo] = useState(null);
    const [uptime, setUptime] = useState(null);
    const [loading, setLoading] = useState(true);

    const [allTournaments, setAllTournaments] = useState([]);
    const [footballTournaments, setFootballTournaments] = useState([]);
    const [selectedTournamentId, setSelectedTournamentId] = useState('');
    const [refreshingStandings, setRefreshingStandings] = useState(false);

    // Predictions card: tournament → future NS football match
    const [predictionTournamentId, setPredictionTournamentId] = useState('');
    const [predictionMatches, setPredictionMatches] = useState([]);
    const [predictionMatchId, setPredictionMatchId] = useState('');
    const [loadingPredictionMatches, setLoadingPredictionMatches] = useState(false);
    const [refreshingPredictions, setRefreshingPredictions] = useState(false);

    // Recalculate card: tournament → round → match
    const [recalcTournamentId, setRecalcTournamentId] = useState('');
    const [recalcAllMatches, setRecalcAllMatches] = useState([]);
    const [recalcRound, setRecalcRound] = useState('');
    const [recalcMatchId, setRecalcMatchId] = useState('');
    const [loadingRecalcMatches, setLoadingRecalcMatches] = useState(false);
    const [recalculatingPoints, setRecalculatingPoints] = useState(false);

    // Achievements recalculation
    const [recalculatingAchievements, setRecalculatingAchievements] = useState(false);

    // Prediction reminder notifications
    const [sendingReminders, setSendingReminders] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // 1. Načteme Health (obsahuje DB, Disk atd.)
                const healthData = await adminService.getHealth();
                setHealth(healthData);

                // 2. Načteme Info (verze appky)
                try {
                    const infoData = await adminService.getInfo();
                    setInfo(infoData);
                } catch (e) {
                    console.warn("Info endpoint not available");
                }

                // 3. Načteme Uptime (metrika process.uptime)
                try {
                    const uptimeMetric = await adminService.getSpecificMetric('process.uptime');
                    setUptime(uptimeMetric.measurements[0].value);
                } catch (e) {
                    console.warn("Metrics not available");
                }

            } catch (error) {
                console.error("Failed to load admin data", error);
                toast.error("Nepodařilo se načíst data. Jste přihlášen jako ADMIN?");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        const fetchTournaments = async () => {
            try {
                const data = await tournamentService.getAllTournaments();
                const sorted = (Array.isArray(data) ? data : [])
                    .slice()
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                setAllTournaments(sorted);

                const football = sorted.filter(t => (t.sportType || '').toUpperCase() === 'FOOTBALL');
                setFootballTournaments(football);
                if (football.length > 0) {
                    setSelectedTournamentId(String(football[0].id));
                }
            } catch (e) {
                console.warn('Failed to load tournaments for admin actions', e);
            }
        };
        fetchTournaments();
    }, []);

    // Load future NS football matches when predictions tournament is selected
    useEffect(() => {
        if (!predictionTournamentId) {
            setPredictionMatches([]);
            setPredictionMatchId('');
            return;
        }
        const fetchMatches = async () => {
            setLoadingPredictionMatches(true);
            try {
                const data = await matchService.getMatchesBySportAndTournament('FOOTBALL', predictionTournamentId);
                const now = Date.now();
                const future = (Array.isArray(data) ? data : [])
                    .filter(m => m.matchStatus === 'NS' && m.date && new Date(m.date).getTime() > now)
                    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                setPredictionMatches(future);
                setPredictionMatchId(future.length > 0 ? String(future[0].id) : '');
            } catch (e) {
                console.warn('Failed to load matches for predictions card', e);
                setPredictionMatches([]);
                setPredictionMatchId('');
            } finally {
                setLoadingPredictionMatches(false);
            }
        };
        fetchMatches();
    }, [predictionTournamentId]);

    // Load recalculable matches when recalc tournament is selected.
    // Backend (admin endpoint) vrací skončené i probíhající zápasy, seřazené DESC.
    useEffect(() => {
        if (!recalcTournamentId) {
            setRecalcAllMatches([]);
            setRecalcRound('');
            setRecalcMatchId('');
            return;
        }
        const tournament = allTournaments.find(t => String(t.id) === String(recalcTournamentId));
        if (!tournament) return;

        const fetchMatches = async () => {
            setLoadingRecalcMatches(true);
            try {
                const data = await adminService.getRecalculableMatchesForTournament(tournament.sportType, recalcTournamentId);
                setRecalcAllMatches(Array.isArray(data) ? data : []);
                setRecalcRound('');
                setRecalcMatchId('');
            } catch (e) {
                console.warn('Failed to load matches for recalc card', e);
                setRecalcAllMatches([]);
                setRecalcRound('');
                setRecalcMatchId('');
            } finally {
                setLoadingRecalcMatches(false);
            }
        };
        fetchMatches();
    }, [recalcTournamentId, allTournaments]);

    const handleRefreshStandings = async () => {
        if (!selectedTournamentId) {
            toast.error('Vyberte turnaj');
            return;
        }
        setRefreshingStandings(true);
        try {
            const message = await adminService.refreshStandings(selectedTournamentId);
            toast.success(message || 'Standings byly aktualizovány');
        } catch (error) {
            console.error('Failed to refresh standings', error);
            const detail = error?.response?.data || error?.message || 'Neznámá chyba';
            toast.error(`Nepodařilo se aktualizovat standings: ${detail}`);
        } finally {
            setRefreshingStandings(false);
        }
    };

    const handleRefreshPredictions = async () => {
        const id = String(predictionMatchId).trim();
        if (!id || Number.isNaN(Number(id))) {
            toast.error('Zadejte platné Match ID');
            return;
        }
        setRefreshingPredictions(true);
        try {
            const data = await adminService.refreshPredictionsForMatch(id);
            const fetchedAt = data?.fetchedAt ? new Date(data.fetchedAt).toLocaleString() : '—';
            toast.success(`Predikce uložena (fetched ${fetchedAt})`);
        } catch (error) {
            console.error('Failed to refresh predictions', error);
            const detail = error?.response?.data || error?.message || 'Neznámá chyba';
            toast.error(`Nepodařilo se stáhnout predikci: ${detail}`);
        } finally {
            setRefreshingPredictions(false);
        }
    };

    const handleRecalculatePoints = async () => {
        const id = String(recalcMatchId).trim();
        if (!id || Number.isNaN(Number(id))) {
            toast.error('Zadejte platné Match ID');
            return;
        }
        setRecalculatingPoints(true);
        try {
            const message = await adminService.recalculatePointsForMatch(id);
            toast.success(message || `Body přepočítány pro zápas ${id}`);
        } catch (error) {
            console.error('Failed to recalculate points', error);
            const detail = error?.response?.data || error?.message || 'Neznámá chyba';
            toast.error(`Nepodařilo se přepočítat body: ${detail}`);
        } finally {
            setRecalculatingPoints(false);
        }
    };

    const handleRecalculateAchievements = async () => {
        if (!window.confirm('Opravdu chcete přepočítat všechny achievementy? Tato operace nejdříve smaže stávající data o úspěších uživatelů.')) {
            return;
        }
        setRecalculatingAchievements(true);
        try {
            await adminService.recalculateAchievements();
            toast.success('Achievementy byly úspěšně přepočítány');
        } catch (error) {
            console.error('Failed to recalculate achievements', error);
            const detail = error?.response?.data || error?.message || 'Neznámá chyba';
            toast.error(`Nepodařilo se přepočítat achievementy: ${detail}`);
        } finally {
            setRecalculatingAchievements(false);
        }
    };

    const handleRunPredictionReminders = async () => {
        if (!window.confirm('Spustit rozesílání e-mailových upozornění na nenatipované zápasy? Respektuje testovací pojistku (restrict-to-email) na backendu.')) {
            return;
        }
        setSendingReminders(true);
        try {
            const message = await adminService.runPredictionReminders();
            toast.success(message || 'Job upozornění byl spuštěn');
        } catch (error) {
            console.error('Failed to run prediction reminders', error);
            const detail = error?.response?.data || error?.message || 'Neznámá chyba';
            toast.error(`Nepodařilo se spustit job upozornění: ${detail}`);
        } finally {
            setSendingReminders(false);
        }
    };

    const formatMatchLabel = (m) => {
        const home = m.homeTeam?.name || '?';
        const away = m.awayTeam?.name || '?';
        const date = m.date ? new Date(m.date).toLocaleString() : '—';
        const live = IN_PROGRESS_MATCH_STATUSES.includes(m.matchStatus) ? '🔴 ŽIVĚ · ' : '';
        return `${live}${home} – ${away} (${date})`;
    };

    // Distinct rounds in recalc tournament, "—" bucket for matches without round
    const recalcRounds = Array.from(
        new Set(recalcAllMatches.map(m => m.round || '—'))
    ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    const recalcMatchesInRound = recalcRound
        ? recalcAllMatches.filter(m => (m.round || '—') === recalcRound)
        : [];

    const formatUptime = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        return `${h}h ${m}m`;
    };

    if (loading) return <div className="admin-loading">Načítání diagnostiky...</div>;

    return (
        <div className="admin-dashboard-container">
            <h2 className="admin-section-title">Stav systému</h2>
            <div className="admin-grid">
                {/* Karta: Celkový stav */}
                <div className={`admin-card ${health?.status === 'UP' ? 'status-ok' : 'status-error'}`}>
                    <h3>Status Aplikace</h3>
                    <div className="big-status">{health?.status}</div>
                    {uptime && <p>Běží: {formatUptime(uptime)}</p>}
                </div>

                {/* Karta: Informace o verzi */}
                {info && (
                    <div className="admin-card">
                        <h3>Informace</h3>
                        <p><strong>App:</strong> {info.app?.name}</p>
                        <p><strong>Verze:</strong> {info.app?.version}</p>
                        <p><strong>Java:</strong> {info.app?.java?.version}</p>
                    </div>
                )}

                {/* Karta: Databáze */}
                {health?.components?.db && (
                    <div className="admin-card">
                        <h3>Databáze</h3>
                        <p><strong>Status:</strong> {health.components.db.status}</p>
                        <p><strong>DB:</strong> {health.components.db.details?.database}</p>
                    </div>
                )}

                {/* Karta: Disk */}
                {health?.components?.diskSpace && (
                    <div className="admin-card">
                        <h3>Disk Space</h3>
                        <p><strong>Status:</strong> {health.components.diskSpace.status}</p>
                        <p><strong>Volno:</strong> {(health.components.diskSpace.details?.free / 1024 / 1024 / 1024).toFixed(2)} GB</p>
                        <p><strong>Celkem:</strong> {(health.components.diskSpace.details?.total / 1024 / 1024 / 1024).toFixed(2)} GB</p>
                    </div>
                )}
            </div>

            <h2 className="admin-section-title">Nástroje</h2>
            <div className="admin-actions-grid">
                {/* Karta: Standings refresh */}
                <div className="admin-action-card">
                    <div className="admin-action-header">
                        <span className="admin-action-icon"><FiBarChart2 /></span>
                        <h3>Tabulka (Standings)</h3>
                    </div>
                    <p className="admin-action-desc">
                        Vyžádá si aktuální tabulku z API-Football pro vybraný fotbalový turnaj a přepíše cache v DB.
                    </p>
                    <div className="admin-action-body">
                        <div className="admin-field">
                            <label>Turnaj</label>
                            <select
                                className="admin-select"
                                value={selectedTournamentId}
                                onChange={(e) => setSelectedTournamentId(e.target.value)}
                                disabled={refreshingStandings || footballTournaments.length === 0}
                            >
                                {footballTournaments.length === 0 ? (
                                    <option value="">Žádné FOOTBALL turnaje</option>
                                ) : (
                                    footballTournaments.map(tournament => (
                                        <option key={tournament.id} value={tournament.id}>
                                            {t(`tournaments.${tournament.name}`, { year: tournament.year })}
                                        </option>
                                    ))
                                )}
                            </select>
                        </div>
                    </div>
                    <button
                        className="admin-action-btn"
                        onClick={handleRefreshStandings}
                        disabled={refreshingStandings || !selectedTournamentId}
                    >
                        {refreshingStandings ? 'Stahuji…' : <><FiBarChart2 /> Stáhnout standings</>}
                    </button>
                </div>

                {/* Karta: Refresh predikcí pro konkrétní zápas */}
                <div className="admin-action-card">
                    <div className="admin-action-header">
                        <span className="admin-action-icon"><FiTarget /></span>
                        <h3>Predikce zápasu</h3>
                    </div>
                    <p className="admin-action-desc">
                        Vyžádá si predikci z API-Football pro vybraný budoucí fotbalový zápas a uloží ji do match_predictions. Ignoruje 30min throttle scheduleru.
                    </p>
                    <div className="admin-action-body">
                        <div className="admin-field">
                            <label>Turnaj</label>
                            <select
                                className="admin-select"
                                value={predictionTournamentId}
                                onChange={(e) => setPredictionTournamentId(e.target.value)}
                                disabled={refreshingPredictions || footballTournaments.length === 0}
                            >
                                <option value="">— Vyberte turnaj —</option>
                                {footballTournaments.map(t => (
                                    <option key={t.id} value={t.id}>
                                        {t.name}{t.year ? ` (${t.year})` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="admin-field">
                            <label>Zápas</label>
                            <select
                                className="admin-select"
                                value={predictionMatchId}
                                onChange={(e) => setPredictionMatchId(e.target.value)}
                                disabled={refreshingPredictions || loadingPredictionMatches || !predictionTournamentId || predictionMatches.length === 0}
                            >
                                {loadingPredictionMatches ? (
                                    <option value="">Načítám zápasy…</option>
                                ) : !predictionTournamentId ? (
                                    <option value="">— Nejdřív turnaj —</option>
                                ) : predictionMatches.length === 0 ? (
                                    <option value="">Žádné budoucí zápasy</option>
                                ) : (
                                    predictionMatches.map(m => (
                                        <option key={m.id} value={m.id}>
                                            {formatMatchLabel(m)}
                                        </option>
                                    ))
                                )}
                            </select>
                        </div>
                    </div>
                    <button
                        className="admin-action-btn"
                        onClick={handleRefreshPredictions}
                        disabled={refreshingPredictions || !predictionMatchId}
                    >
                        {refreshingPredictions ? 'Stahuji…' : <><FiTarget /> Stáhnout predikci</>}
                    </button>
                </div>

                {/* Karta: Přepočet bodů pro konkrétní zápas */}
                <div className="admin-action-card">
                    <div className="admin-action-header">
                        <span className="admin-action-icon"><FiRefreshCw /></span>
                        <h3>Přepočet bodů zápasu</h3>
                    </div>
                    <p className="admin-action-desc">
                        Projede všechny tipy vybraného zápasu přes scoring service a přepíše body uživatelů. Použij po ruční opravě skóre v DB. V nabídce jsou skončené (FT / AET / PEN / AOT / AP) i probíhající (živé) zápasy.
                    </p>
                    <div className="admin-action-body">
                        <div className="admin-field">
                            <label>Turnaj</label>
                            <select
                                className="admin-select"
                                value={recalcTournamentId}
                                onChange={(e) => setRecalcTournamentId(e.target.value)}
                                disabled={recalculatingPoints || allTournaments.length === 0}
                            >
                                <option value="">— Vyberte turnaj —</option>
                                {allTournaments.map(t => (
                                    <option key={t.id} value={t.id}>
                                        {t.name}{t.year ? ` (${t.year})` : ''} [{t.sportType}]
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="admin-field">
                            <label>Kolo</label>
                            <select
                                className="admin-select"
                                value={recalcRound}
                                onChange={(e) => { setRecalcRound(e.target.value); setRecalcMatchId(''); }}
                                disabled={recalculatingPoints || loadingRecalcMatches || !recalcTournamentId || recalcRounds.length === 0}
                            >
                                {loadingRecalcMatches ? (
                                    <option value="">Načítám kola…</option>
                                ) : !recalcTournamentId ? (
                                    <option value="">— Nejdřív turnaj —</option>
                                ) : recalcRounds.length === 0 ? (
                                    <option value="">Žádné skončené ani probíhající zápasy</option>
                                ) : (
                                    <>
                                        <option value="">— Vyberte kolo —</option>
                                        {recalcRounds.map(r => (
                                            <option key={r} value={r}>{r === '—' ? 'Bez kola' : r}</option>
                                        ))}
                                    </>
                                )}
                            </select>
                        </div>
                        <div className="admin-field">
                            <label>Zápas</label>
                            <select
                                className="admin-select"
                                value={recalcMatchId}
                                onChange={(e) => setRecalcMatchId(e.target.value)}
                                disabled={recalculatingPoints || !recalcRound || recalcMatchesInRound.length === 0}
                            >
                                {!recalcRound ? (
                                    <option value="">— Nejdřív kolo —</option>
                                ) : recalcMatchesInRound.length === 0 ? (
                                    <option value="">Žádné zápasy v kole</option>
                                ) : (
                                    <>
                                        <option value="">— Vyberte zápas —</option>
                                        {recalcMatchesInRound.map(m => (
                                            <option key={m.id} value={m.id}>
                                                {formatMatchLabel(m)}
                                            </option>
                                        ))}
                                    </>
                                )}
                            </select>
                        </div>
                    </div>
                    <button
                        className="admin-action-btn"
                        onClick={handleRecalculatePoints}
                        disabled={recalculatingPoints || !recalcMatchId}
                    >
                        {recalculatingPoints ? 'Přepočítávám…' : <><FiRefreshCw /> Přepočítat body</>}
                    </button>
                </div>

                {/* Karta: Přepočet achievementů */}
                <div className="admin-action-card is-danger">
                    <div className="admin-action-header">
                        <span className="admin-action-icon"><FiAward /></span>
                        <h3>Přepočet achievementů</h3>
                    </div>
                    <p className="admin-action-desc">
                        Kompletní přepočet všech úspěchů uživatelů z historických dat zápasů a predikcí. Pozor: operace nejdřív smaže stávající data o achievementech.
                    </p>
                    <button
                        className="admin-action-btn is-danger"
                        onClick={handleRecalculateAchievements}
                        disabled={recalculatingAchievements}
                    >
                        {recalculatingAchievements ? 'Přepočítávám…' : <><FiAward /> Přepočítat achievementy</>}
                    </button>
                </div>

                {/* Karta: Upozornění na nenatipované zápasy */}
                <div className="admin-action-card">
                    <div className="admin-action-header">
                        <span className="admin-action-icon"><FiBell /></span>
                        <h3>Upozornění na tipy</h3>
                    </div>
                    <p className="admin-action-desc">
                        Ručně spustí job, který uživatelům rozešle e-mailové upozornění na nenatipované zápasy začínající v jejich nastaveném okně. Na stejný zápas přijde upozornění jen jednou. Na backendu lze rozesílání omezit jen na jeden e-mail (testovací pojistka).
                    </p>
                    <button
                        className="admin-action-btn"
                        onClick={handleRunPredictionReminders}
                        disabled={sendingReminders}
                    >
                        {sendingReminders ? 'Odesílám…' : <><FiBell /> Rozeslat upozornění</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;